// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  Timestamp,
  addDoc,
  DocumentReference,
  onSnapshot,
  updateDoc,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";

import { getAuth, signInWithCustomToken, signOut } from "firebase/auth";

import {
  GameDetails,
  GreenCardRating,
  PlayerDetails,
  RatingScore,
  UpdateGameDetails,
  UpdatePlayerDetails,
} from "../types/Game";

// https://firebase.google.com/docs/web/setup#available-libraries

// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyAIS9ptMF0wUFG9jxa17Q05fLzHG5gtQJ0",
  authDomain: "ellas-avanzan-juego.firebaseapp.com",
  projectId: "ellas-avanzan-juego",
  storageBucket: "ellas-avanzan-juego.appspot.com",
  messagingSenderId: "145897630941",
  appId: "1:145897630941:web:9d5996eedd0b8ad4173f34",
  measurementId: "G-MXYCBNJ0CN",
};

export enum FirebaseCollection {
  Games = "games",
  Players = "players",
  GreenCardRating = "GreenCardRating",
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

let unsubscribeListenNewPlayers: any;
let unsubscribeListenGame: any;
let unsubscribeCardRating: any;

const authenticateUser = async (token: string) => {
  const res = await signInWithCustomToken(auth, token);
  console.log(res);
};

const logoutUser = async () => {
  const res = await signOut(auth);
  console.log(res);
};

const addDocument = async (
  collectionName: FirebaseCollection,
  data: any
): Promise<DocumentReference> => {
  const gamesCollection = collection(db, collectionName);
  const newGameRef = await addDoc(gamesCollection, {
    ...data,
    createdAt: Timestamp.now(),
  });

  return newGameRef;
};

const updateGame = async (
  gameId: string,
  data: UpdateGameDetails
): Promise<void> => {
  const q = query(
    collection(db, FirebaseCollection.Games),
    where("gameId", "==", gameId)
  );
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach(async (doc) => {
    const gameRef = doc.ref;
    await updateDoc(gameRef, data);
  });
};

const updateGamePlayer = async (
  gameId: string,
  userId: string,
  data: UpdatePlayerDetails
): Promise<void> => {
  const q = query(
    collection(db, FirebaseCollection.Players),
    where("gameId", "==", gameId),
    where("userId", "==", userId)
  );
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach(async (doc) => {
    const gameRef = doc.ref;
    await updateDoc(gameRef, data);
  });
};

const listenNewPlayers = (gameId: string, onChange: Function): void => {
  const q = query(
    collection(db, FirebaseCollection.Players),
    where("gameId", "==", gameId)
  );
  const players: PlayerDetails[] = [];
  let newPlayer: PlayerDetails | null = null;
  unsubscribeListenNewPlayers = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added") {
        newPlayer = change.doc.data() as PlayerDetails;
      }
      players.push(change.doc.data() as PlayerDetails);
    });
    onChange({ players, newPlayer });
  });
};

const listenGame = (gameId: string, onChange: Function): void => {
  const q = query(
    collection(db, FirebaseCollection.Games),
    where("gameId", "==", gameId)
  );

  unsubscribeListenGame = onSnapshot(q, (snapshot) => {
    snapshot.forEach((doc) => {
      const game = doc.data() as GameDetails;
      onChange(game);
    });
  });
};

const getPlayersByGame = async (gameId: string): Promise<PlayerDetails[]> => {
  const q = query(
    collection(db, FirebaseCollection.Players),
    where("gameId", "==", gameId)
  );
  const playersList: PlayerDetails[] = [];
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    playersList.push(doc.data() as PlayerDetails);
  });

  return playersList;
};

const getGame = async (gameId: string): Promise<GameDetails | null> => {
  const q = query(
    collection(db, FirebaseCollection.Games),
    where("gameId", "==", gameId)
  );
  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return doc.data() as GameDetails;
  }
  return null;
};

const isPlayerInGame = async (userId: string, gameId: string) => {
  const q = query(
    collection(db, FirebaseCollection.Players),
    where("gameId", "==", gameId),
    where("userId", "==", userId)
  );
  const playersList: PlayerDetails[] = [];
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    playersList.push(doc.data() as PlayerDetails);
  });

  return playersList.length > 0 ? true : false;
};

const listentGreenCardRating = async (
  userId: string,
  gameId: string,
  cardId: string,
  onChange: Function
) => {
  const q = query(
    collection(db, FirebaseCollection.GreenCardRating),
    where("gameId", "==", gameId),
    where("userId", "==", userId),
    where("cardId", "==", cardId)
  );
  let score = 0;
  unsubscribeCardRating = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      const rating = change.doc.data() as GreenCardRating;
      console.log("listen:", change.doc.data());
      score = Number(score) + Number(rating.score);
    });
    const media = score / snapshot.docChanges().length;
    onChange(media);
  });
};

const getGreenCardRating = async (
  userId: string,
  gameId: string,
  cardId: string
): Promise<RatingScore> => {
  const q = query(
    collection(db, FirebaseCollection.GreenCardRating),
    where("gameId", "==", gameId),
    where("userId", "==", userId),
    where("cardId", "==", cardId)
  );

  let score = 0;
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return { total: 0, votes: 0 };
  }
  querySnapshot.forEach((doc) => {
    const rating = doc.data() as GreenCardRating;
    score = score + rating.score;
  });

  score =
    querySnapshot.docs.length > 0 ? score / querySnapshot.docs.length : score;

  return {
    total: score,
    votes: querySnapshot.docs.length,
  };
};

const getAllGreenCardRatings = async (userId: string, gameId: string) => {
  const q = query(
    collection(db, FirebaseCollection.GreenCardRating),
    where("gameId", "==", gameId),
    where("userId", "==", userId)
  );

  let score = 0;
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return score;
  }
  querySnapshot.forEach((doc) => {
    const rating = doc.data() as GreenCardRating;
    score = score + rating.score;
  });

  return querySnapshot.docs.length > 0
    ? score / querySnapshot.docs.length
    : score;
};

const removePlayerFromGame = async (
  userId: string,
  gameId: string
): Promise<void> => {
  const q = query(
    collection(db, FirebaseCollection.Players),
    where("gameId", "==", gameId),
    where("userId", "==", userId)
  );
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    const docRef: DocumentReference = doc.ref;
    deleteDoc(docRef);
  });
};

const stopWatchingAll = () => {
  unsubscribeListenNewPlayers();
};

export default {
  app,
  db,
  addDocument,
  isPlayerInGame,
  FirebaseCollection,
  listenNewPlayers,
  getPlayersByGame,
  getAllGreenCardRatings,
  getGame,
  listenGame,
  updateGame,
  removePlayerFromGame,
  updateGamePlayer,
  getGreenCardRating,
  listentGreenCardRating,
  authenticateUser,
  logoutUser,
};
